import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { DropResult } from '@hello-pangea/dnd';
import PageHeader from '../components/common/PageHeader';
import SandboxFreigabeToggle from '../components/common/SandboxFreigabeToggle';
import AppBreadcrumbs from '../components/common/AppBreadcrumbs';
import FormSkeleton from '../components/common/FormSkeleton';
import ConfirmDialog from '../components/common/ConfirmDialog';
import HistorieTimeline from '../components/common/HistorieTimeline';
import VersionsHistorie from '../components/common/VersionsHistorie';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import {
  SCHWACHSTELLEN_DELETE,
  SCHWACHSTELLEN_DELETE_OWN,
  SCHWACHSTELLEN_EDIT,
} from '../constants/permissions';
import { auditsService } from '../services/auditsService';
import { durchfuehrungszeitraeumeService } from '../services/durchfuehrungszeitraeumeService';
import { filesService } from '../services/filesService';
import { schwachstellenService } from '../services/schwachstellenService';
import { Audit, Durchfuehrungszeitraum, FileEntity, SchwachstelleList } from '../types';
import { useNotification } from '../hooks/NotificationContext';
import AuditCreateForm from './audit-detail/AuditCreateForm';
import AuditFindingsSection from './audit-detail/AuditFindingsSection';
import AuditFirewallSection from './audit-detail/AuditFirewallSection';
import { isFirewallAudit } from '../utils/firewallFormat';
import SchwachstelleTransferDialog from './audit-detail/SchwachstelleTransferDialog';
import AuditMetaSummaryCard from './audit-detail/AuditMetaSummaryCard';
import DiskussionContextLink from '../components/diskussion/DiskussionContextLink';
import AuditReportsSection from './audit-detail/AuditReportsSection';
import AuditRichTextSection from './audit-detail/AuditRichTextSection';
import AuditStatsSection from './audit-detail/AuditStatsSection';
import ZeitraumDialog from './audit-detail/ZeitraumDialog';
import { projektnummerFehler } from './audit-detail/projektnummer';
import {
  createEmptyZeitraumForm,
  createInitialAuditForm,
  createPendingZeitraum,
  mapAuditToFormData,
} from './audit-detail/auditDetailMappers';
import {
  buildKritData,
  buildReviewData,
} from './audit-detail/auditDetailStats';
import ReportReviewWarningDialog from './audit-detail/ReportReviewWarningDialog';
import { unreviewedSchwachstellen } from '../utils/reportReviewGate';
import { useAuditFindings } from './audit-detail/hooks/useAuditFindings';
import { useAuditLookups } from './audit-detail/hooks/useAuditLookups';
import { useAuditReports } from './audit-detail/hooks/useAuditReports';
import { useAuditZeitraeume } from './audit-detail/hooks/useAuditZeitraeume';
import {
  getPendingZeitraumDateError,
  reorderArray,
} from './audit-detail/auditDetailUtils';
import {
  AuditFormData,
  PendingZeitraum,
  ZeitraumFormState,
} from './audit-detail/auditDetailTypes';

type ExpandedPanelKey =
  | 'managementSummary'
  | 'stats'
  | 'vorbemerkungen'
  | 'firewall'
  | 'schwachstellen'
  | 'reports';

const AuditDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { hasPermission, hasRole } = usePermissions();
  const { showSuccess, showError } = useNotification();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const isNew = id === 'neu';
  const auditId = !isNew && id ? Number.parseInt(id, 10) : null;
  const preselectedProjektId = isNew
    ? Number.parseInt(searchParams.get('projekt_id') || '0', 10)
    : 0;

  const canTransferSchwachstelle = hasPermission(SCHWACHSTELLEN_EDIT);
  const canDeleteSchwachstelle = (schwachstelle: { reporter?: { id?: number } | null }) =>
    hasPermission(SCHWACHSTELLEN_DELETE) ||
    (hasPermission(SCHWACHSTELLEN_DELETE_OWN) && schwachstelle.reporter?.id === user?.id);

  const [audit, setAudit] = useState<Audit | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<FileEntity[]>([]);
  const [loadingFilesToDelete, setLoadingFilesToDelete] = useState(false);
  const [managementSummaryDirty, setManagementSummaryDirty] = useState(false);
  const [vorbemerkungenDirty, setVorbemerkungenDirty] = useState(false);
  const [historieOpen, setHistorieOpen] = useState(false);
  const [versionenOpen, setVersionenOpen] = useState(false);
  const [reportWarningOpen, setReportWarningOpen] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<Record<ExpandedPanelKey, boolean>>({
    managementSummary: false,
    stats: false,
    vorbemerkungen: false,
    firewall: true,
    schwachstellen: true,
    reports: false,
  });
  const [formData, setFormData] = useState<AuditFormData>(
    createInitialAuditForm(preselectedProjektId),
  );
  const [projektnummerAttempted, setProjektnummerAttempted] = useState(false);

  const {
    projekte,
    mitarbeiter,
    auditTypes,
    auditStatuses,
    loadingLookups,
  } = useAuditLookups();
  const {
    schwachstellen,
    setSchwachstellen,
    loadingSchwachstellen,
    reloadSchwachstellen,
  } = useAuditFindings(auditId);
  const { files, loadingFiles, loadFiles } = useAuditReports(auditId);
  const { zeitraeume, loadZeitraeume } = useAuditZeitraeume(auditId);

  const handleAuditPasteImage = useCallback(
    async (file: File): Promise<string | null> => {
      if (auditId == null) return null;
      try {
        const ent = await filesService.upload(file, undefined, undefined, auditId, undefined);
        return filesService.getPreviewUrl(ent.id);
      } catch {
        showError('Bild aus Zwischenablage konnte nicht hochgeladen werden.');
        return null;
      }
    },
    [auditId, showError],
  );

  const [pendingZeitraeume, setPendingZeitraeume] = useState<PendingZeitraum[]>([]);
  const [pendingTempIdCounter, setPendingTempIdCounter] = useState(0);

  const [zeitraumDialogOpen, setZeitraumDialogOpen] = useState(false);
  const [editingZeitraum, setEditingZeitraum] = useState<Durchfuehrungszeitraum | null>(null);
  const [zeitraumForm, setZeitraumForm] = useState<ZeitraumFormState>(
    createEmptyZeitraumForm(),
  );
  const [deleteZeitraumDialogOpen, setDeleteZeitraumDialogOpen] = useState(false);
  const [deletingZeitraumId, setDeletingZeitraumId] = useState<number | null>(null);

  const [typMenuAnchor, setTypMenuAnchor] = useState<HTMLElement | null>(null);
  const [projektMenuAnchor, setProjektMenuAnchor] = useState<HTMLElement | null>(null);
  const [pentesterMenuAnchor, setPentesterMenuAnchor] = useState<HTMLElement | null>(null);

  const [deleteSchwachstelleDialogOpen, setDeleteSchwachstelleDialogOpen] =
    useState(false);
  const [deletingSchwachstelleId, setDeletingSchwachstelleId] = useState<number | null>(null);
  const [filesToDeleteSchwachstelle, setFilesToDeleteSchwachstelle] = useState<FileEntity[]>([]);
  const [transferSchwachstelle, setTransferSchwachstelle] = useState<SchwachstelleList | null>(null);

  const [deleteReportDialogOpen, setDeleteReportDialogOpen] = useState(false);
  const [deletingReportIds, setDeletingReportIds] = useState<number[]>([]);

  useEffect(() => {
    if (!isNew || formData.audit_status_id || auditStatuses.length === 0) return;

    const geplantStatus = auditStatuses.find(
      (status) => status.name.toLowerCase() === 'geplant',
    );

    if (geplantStatus) {
      setFormData((prev) => ({ ...prev, audit_status_id: geplantStatus.id }));
    }
  }, [isNew, formData.audit_status_id, auditStatuses]);

  useEffect(() => {
    if (!auditId) return;

    const loadAudit = async () => {
      try {
        setLoading(true);
        const auditData = await auditsService.get(auditId);
        setAudit(auditData);
        setFormData(mapAuditToFormData(auditData));
        setManagementSummaryDirty(false);
        setVorbemerkungenDirty(false);
      } catch (error) {
        console.error('Fehler beim Laden des Audits:', error);
      } finally {
        setLoading(false);
      }
    };

    void loadAudit();
  }, [auditId]);

  const handleAccordionChange =
    (panel: ExpandedPanelKey) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) => ({ ...prev, [panel]: isExpanded }));
    };

  const handleFirewallSectionToggle = useCallback((_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels((prev) => ({ ...prev, firewall: isExpanded }));
  }, []);

  const canFirewallReview = useMemo(
    () => hasRole('Pentester') && Boolean(audit?.mitarbeiter_team?.some((member) => member.id === user?.id)),
    [audit?.mitarbeiter_team, hasRole, user?.id],
  );

  const handleInputChange = (
    field: keyof AuditFormData,
    value: string | number | number[] | undefined,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleStartEditing = () => {
    setProjektnummerAttempted(false);
    setEditing(true);
  };

  const handleCancelEditing = () => {
    if (audit) {
      setFormData(mapAuditToFormData(audit));
    }
    setManagementSummaryDirty(false);
    setVorbemerkungenDirty(false);
    setProjektnummerAttempted(false);
    setEditing(false);
  };

  const handleSave = async () => {
    const fehler = projektnummerFehler(formData.projektnummer || '', true);
    if (fehler) {
      setProjektnummerAttempted(true);
      return;
    }

    try {
      setSaving(true);
      const preflightDatum = formData.preflight_datum || undefined;

      if (isNew) {
        const created = await auditsService.create({
          ...formData,
          projektnummer: formData.projektnummer.trim(),
          preflight_datum: preflightDatum,
        });
        const validPending = pendingZeitraeume.filter(
          (zeitraum) =>
            zeitraum.startdatum &&
            zeitraum.enddatum &&
            !getPendingZeitraumDateError(zeitraum),
        );

        if (validPending.length > 0) {
          await Promise.all(
            validPending.map((zeitraum) =>
              durchfuehrungszeitraeumeService.create({
                startdatum: zeitraum.startdatum,
                enddatum: zeitraum.enddatum,
                beschreibung: zeitraum.beschreibung || undefined,
                audit_id: created.id,
                mitarbeiter_ids: zeitraum.mitarbeiter_ids,
              }),
            ),
          );
        }

        showSuccess(
          validPending.length > 0
            ? `Audit mit ${validPending.length} Durchführungszeitraum/-räumen erstellt`
            : 'Audit erfolgreich erstellt',
        );
        setTimeout(() => navigate(`/audits/${created.id}`), 800);
      } else if (auditId) {
        const updated = await auditsService.update(auditId, {
          ...formData,
          projektnummer: formData.projektnummer.trim(),
          preflight_datum: preflightDatum,
        });
        setAudit(updated);
        setFormData(mapAuditToFormData(updated));
        showSuccess('Audit erfolgreich aktualisiert');
        setProjektnummerAttempted(false);
        setEditing(false);
      }
    } catch {
      // Fehler werden zentral behandelt
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!auditId) return;

    try {
      await auditsService.delete(auditId);
      showSuccess('Audit erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setTimeout(() => navigate('/audits'), 1000);
    } catch {
      setDeleteDialogOpen(false);
    }
  };

  const saveAuditField = async (
    field: 'management_summary' | 'vorbemerkungen',
    successMessage: string,
    onAfterSave: () => void,
  ) => {
    if (!auditId) return;

    try {
      setSaving(true);
      const updated = await auditsService.update(auditId, { [field]: formData[field] });
      setAudit(updated);
      onAfterSave();
      showSuccess(successMessage);
    } catch {
      // Fehler werden zentral behandelt
    } finally {
      setSaving(false);
    }
  };

  const unreviewedFindings = useMemo(
    () => unreviewedSchwachstellen(schwachstellen),
    [schwachstellen],
  );

  const handleGenerateReport = async () => {
    if (!auditId) return;

    try {
      setSaving(true);
      await auditsService.generateReport(auditId);
      showSuccess('Report erfolgreich generiert (DOCX + PDF)');
      await loadFiles();
    } catch {
      // Fehler werden zentral behandelt
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateReportRequest = () => {
    if (unreviewedFindings.length > 0) {
      setReportWarningOpen(true);
      return;
    }
    void handleGenerateReport();
  };

  const handleGenerateReportAnyway = async () => {
    await handleGenerateReport();
    setReportWarningOpen(false);
  };

  const handleAddPendingZeitraum = () => {
    const newId = pendingTempIdCounter + 1;
    setPendingTempIdCounter(newId);
    setPendingZeitraeume((prev) => [...prev, createPendingZeitraum(newId)]);
  };

  const handleUpdatePendingZeitraum = (
    tempId: number,
    field: keyof PendingZeitraum,
    value: string | number[],
  ) => {
    setPendingZeitraeume((prev) =>
      prev.map((zeitraum) =>
        zeitraum.tempId === tempId ? { ...zeitraum, [field]: value } : zeitraum,
      ),
    );
  };

  const handleRemovePendingZeitraum = (tempId: number) => {
    setPendingZeitraeume((prev) => prev.filter((zeitraum) => zeitraum.tempId !== tempId));
  };

  const handleOpenZeitraumDialog = (zeitraum?: Durchfuehrungszeitraum) => {
    if (zeitraum) {
      setEditingZeitraum(zeitraum);
      setZeitraumForm({
        startdatum: zeitraum.startdatum.slice(0, 10),
        enddatum: zeitraum.enddatum.slice(0, 10),
        beschreibung: zeitraum.beschreibung || '',
        mitarbeiter_ids: zeitraum.mitarbeiter_team.map((entry) => entry.id),
      });
    } else {
      setEditingZeitraum(null);
      setZeitraumForm(createEmptyZeitraumForm());
    }

    setZeitraumDialogOpen(true);
  };

  const handleCloseZeitraumDialog = () => {
    setZeitraumDialogOpen(false);
    setEditingZeitraum(null);
    setZeitraumForm(createEmptyZeitraumForm());
  };

  const zeitraumDateError = getPendingZeitraumDateError(zeitraumForm);

  const handleSaveZeitraum = async () => {
    if (!auditId || zeitraumDateError) return;

    try {
      if (editingZeitraum) {
        await durchfuehrungszeitraeumeService.update(editingZeitraum.id, {
          startdatum: zeitraumForm.startdatum,
          enddatum: zeitraumForm.enddatum,
          beschreibung: zeitraumForm.beschreibung || undefined,
          mitarbeiter_ids: zeitraumForm.mitarbeiter_ids,
        });
        showSuccess('Zeitraum erfolgreich aktualisiert');
      } else {
        await durchfuehrungszeitraeumeService.create({
          startdatum: zeitraumForm.startdatum,
          enddatum: zeitraumForm.enddatum,
          beschreibung: zeitraumForm.beschreibung || undefined,
          audit_id: auditId,
          mitarbeiter_ids: zeitraumForm.mitarbeiter_ids,
        });
        showSuccess('Zeitraum erfolgreich erstellt');
      }

      handleCloseZeitraumDialog();
      await loadZeitraeume();
    } catch {
      // Fehler werden zentral behandelt
    }
  };

  const handleConfirmDeleteZeitraum = (zeitraumId: number) => {
    setDeletingZeitraumId(zeitraumId);
    setDeleteZeitraumDialogOpen(true);
  };

  const handleDeleteZeitraum = async () => {
    if (!deletingZeitraumId) return;

    try {
      await durchfuehrungszeitraeumeService.delete(deletingZeitraumId);
      showSuccess('Zeitraum erfolgreich gelöscht');
      setDeleteZeitraumDialogOpen(false);
      setDeletingZeitraumId(null);
      await loadZeitraeume();
    } catch {
      setDeleteZeitraumDialogOpen(false);
    }
  };

  const handleConfirmDeleteSchwachstelle = async (schwachstelleId: number) => {
    setDeletingSchwachstelleId(schwachstelleId);
    try {
      const filesForDelete = await schwachstellenService.getFilesToDelete(schwachstelleId);
      setFilesToDeleteSchwachstelle(filesForDelete);
    } catch {
      setFilesToDeleteSchwachstelle([]);
    }
    setDeleteSchwachstelleDialogOpen(true);
  };

  const handleDeleteSchwachstelle = async () => {
    if (!deletingSchwachstelleId) return;

    try {
      await schwachstellenService.delete(deletingSchwachstelleId);
      showSuccess('Schwachstelle erfolgreich gelöscht');
      setDeleteSchwachstelleDialogOpen(false);
      setDeletingSchwachstelleId(null);
      await reloadSchwachstellen();
    } catch {
      setDeleteSchwachstelleDialogOpen(false);
    }
  };

  const handleConfirmDeleteReport = (reportIds: number[]) => {
    setDeletingReportIds(reportIds);
    setDeleteReportDialogOpen(true);
  };

  const handleDeleteReport = async () => {
    if (deletingReportIds.length === 0) return;
    const ids = [...deletingReportIds];

    try {
      for (const id of ids) {
        await filesService.delete(id);
      }
      showSuccess(
        ids.length > 1 ? 'Word- und PDF-Report gelöscht' : 'Report erfolgreich gelöscht',
      );
    } catch {
      showError('Report konnte nicht vollständig gelöscht werden');
    } finally {
      setDeleteReportDialogOpen(false);
      setDeletingReportIds([]);
      await loadFiles();
    }
  };

  const handleFindingsDragEnd = async (result: DropResult) => {
    if (!result.destination || !auditId) return;
    if (result.destination.index === result.source.index) return;

    const previous = schwachstellen;
    const reordered = reorderArray(
      schwachstellen,
      result.source.index,
      result.destination.index,
    ).map((item, idx) => ({
      ...item,
      sort_order: idx,
    }));

    setSchwachstellen(reordered);

    try {
      const saved = await schwachstellenService.reorder(
        auditId,
        reordered.map((entry) => entry.id),
      );
      setSchwachstellen(saved);
      showSuccess('Findings-Reihenfolge gespeichert');
    } catch (error) {
      console.error('Fehler beim Speichern der Findings-Reihenfolge:', error);
      setSchwachstellen(previous);
      showError('Findings-Reihenfolge konnte nicht gespeichert werden');
    }
  };

  const kritData = useMemo(
    () => buildKritData(schwachstellen, isDark),
    [schwachstellen, isDark],
  );
  const reviewData = useMemo(
    () => buildReviewData(schwachstellen),
    [schwachstellen],
  );

  if (loading || loadingLookups) {
    return <FormSkeleton fields={6} hasTabs={!isNew} />;
  }

  return (
    <Box>
      {isNew && (
        <PageHeader
          title="Neues Audit"
          breadcrumbs={[
            ...(preselectedProjektId > 0
              ? [
                  { label: 'Projekte', path: '/projekte' },
                  {
                    label:
                      projekte.find((projekt) => projekt.id === preselectedProjektId)?.name ||
                      'Projekt',
                    path: `/projekte/${preselectedProjektId}`,
                  },
                ]
              : [{ label: 'Audits', path: '/audits' }]),
            { label: 'Neues Audit' },
          ]}
        />
      )}

      {!isNew && audit && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <AppBreadcrumbs
              items={[
                { label: 'Audits', path: '/audits' },
                { label: audit.name || 'Audit' },
              ]}
            />
            {!editing && (
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 2, alignItems: 'center' }}>
                {auditId != null && <SandboxFreigabeToggle kind="audit" id={auditId} />}
                <Tooltip title="Demo Mode">
                  <IconButton
                    component="a"
                    href={`/audits/${id}/demo`}
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
                        e.preventDefault();
                        navigate(`/audits/${id}/demo`);
                      }
                    }}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: '#3dd47e', bgcolor: 'rgba(61,212,126,0.1)' },
                    }}
                  >
                    <SlideshowOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Änderungshistorie">
                  <IconButton
                    size="small"
                    onClick={() => setHistorieOpen(true)}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: '#42a5f5', bgcolor: 'rgba(66,165,245,0.1)' },
                    }}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Versionshistorie">
                  <IconButton
                    size="small"
                    onClick={() => setVersionenOpen(true)}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: '#ab47bc', bgcolor: 'rgba(171,71,188,0.1)' },
                    }}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Löschen">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (!auditId) return;
                      setLoadingFilesToDelete(true);
                      try {
                        const relatedFiles = await auditsService.getFilesToDelete(auditId);
                        setFilesToDelete(relatedFiles);
                        setDeleteDialogOpen(true);
                      } catch {
                        setFilesToDelete([]);
                        setDeleteDialogOpen(true);
                      } finally {
                        setLoadingFilesToDelete(false);
                      }
                    }}
                    disabled={loadingFilesToDelete}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: '#ef5350', bgcolor: 'rgba(239,83,80,0.1)' },
                    }}
                  >
                    {loadingFilesToDelete ? (
                      <CircularProgress size={18} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {auditId != null && (
            <DiskussionContextLink anchor={{ refType: 'audit', refId: auditId }} objectLabel={audit?.name} />
          )}

          <AuditMetaSummaryCard
            isDark={isDark}
            editing={editing}
            saving={saving}
            projektnummerAttempted={projektnummerAttempted}
            formData={formData}
            projekte={projekte}
            auditTypes={auditTypes}
            auditStatuses={auditStatuses}
            mitarbeiter={mitarbeiter}
            zeitraeume={zeitraeume}
            typMenuAnchor={typMenuAnchor}
            projektMenuAnchor={projektMenuAnchor}
            pentesterMenuAnchor={pentesterMenuAnchor}
            onInputChange={handleInputChange}
            onStartEditing={handleStartEditing}
            onCancelEditing={handleCancelEditing}
            onSave={handleSave}
            onSetTypMenuAnchor={setTypMenuAnchor}
            onSetProjektMenuAnchor={setProjektMenuAnchor}
            onSetPentesterMenuAnchor={setPentesterMenuAnchor}
            onOpenZeitraumDialog={handleOpenZeitraumDialog}
            onDeleteZeitraum={handleConfirmDeleteZeitraum}
          />

          <AuditRichTextSection
            isDark={isDark}
            title="Management Summary"
            icon={<SummarizeOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
            value={formData.management_summary || ''}
            dirty={managementSummaryDirty}
            expanded={expandedPanels.managementSummary}
            placeholder="Management Summary verfassen..."
            saving={saving}
            onToggle={handleAccordionChange('managementSummary')}
            onChange={(html) => {
              setFormData((prev) => ({ ...prev, management_summary: html }));
              setManagementSummaryDirty(true);
            }}
            onReset={() => {
              setFormData((prev) => ({
                ...prev,
                management_summary: audit?.management_summary || '',
              }));
              setManagementSummaryDirty(false);
            }}
            onSave={() =>
              saveAuditField('management_summary', 'Management Summary gespeichert', () =>
                setManagementSummaryDirty(false),
              )
            }
            onPasteImage={auditId != null ? handleAuditPasteImage : undefined}
          />

          <AuditRichTextSection
            isDark={isDark}
            title="Vorbemerkungen"
            icon={<GavelOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />}
            value={formData.vorbemerkungen || ''}
            dirty={vorbemerkungenDirty}
            expanded={expandedPanels.vorbemerkungen}
            placeholder="Vorbemerkungen verfassen..."
            saving={saving}
            onToggle={handleAccordionChange('vorbemerkungen')}
            onChange={(html) => {
              setFormData((prev) => ({ ...prev, vorbemerkungen: html }));
              setVorbemerkungenDirty(true);
            }}
            onReset={() => {
              setFormData((prev) => ({
                ...prev,
                vorbemerkungen: audit?.vorbemerkungen || '',
              }));
              setVorbemerkungenDirty(false);
            }}
            onSave={() =>
              saveAuditField('vorbemerkungen', 'Vorbemerkungen gespeichert', () =>
                setVorbemerkungenDirty(false),
              )
            }
            onPasteImage={auditId != null ? handleAuditPasteImage : undefined}
          />

          <AuditFindingsSection
            isDark={isDark}
            expanded={expandedPanels.schwachstellen}
            schwachstellen={schwachstellen}
            loading={loadingSchwachstellen}
            onToggle={handleAccordionChange('schwachstellen')}
            onCreate={() => navigate(`/schwachstellen/neu?audit_id=${auditId}`)}
            onEdit={(schwachstelleId) => navigate(`/schwachstellen/${schwachstelleId}?edit=1`)}
            onTransfer={setTransferSchwachstelle}
            onDelete={handleConfirmDeleteSchwachstelle}
            onDragEnd={handleFindingsDragEnd}
            canTransferSchwachstelle={canTransferSchwachstelle}
            canDeleteSchwachstelle={canDeleteSchwachstelle}
          />

          {isFirewallAudit(audit?.audit_types) && auditId != null && (
            <AuditFirewallSection
              auditId={auditId}
              isDark={isDark}
              expanded={expandedPanels.firewall}
              canReview={canFirewallReview}
              onToggle={handleFirewallSectionToggle}
            />
          )}

          {schwachstellen.length > 0 && (
            <AuditStatsSection
              isDark={isDark}
              expanded={expandedPanels.stats}
              totalFindings={schwachstellen.length}
              kritData={kritData}
              reviewData={reviewData}
              onToggle={handleAccordionChange('stats')}
            />
          )}

          <AuditReportsSection
            isDark={isDark}
            expanded={expandedPanels.reports}
            files={files}
            loadingFiles={loadingFiles}
            saving={saving}
            findingsCount={schwachstellen.length}
            loadingFindings={loadingSchwachstellen}
            onToggle={handleAccordionChange('reports')}
            onGenerateReport={handleGenerateReportRequest}
            onDownload={(fileId) => filesService.download(fileId)}
            onDeleteReport={handleConfirmDeleteReport}
          />
        </>
      )}

      {isNew && (
        <Card>
          <CardContent>
            <AuditCreateForm
              formData={formData}
              isNew={isNew}
              preselectedProjektId={preselectedProjektId}
              auditStatuses={auditStatuses}
              projekte={projekte}
              auditTypes={auditTypes}
              mitarbeiter={mitarbeiter}
              pendingZeitraeume={pendingZeitraeume}
              saving={saving}
              projektnummerAttempted={projektnummerAttempted}
              onInputChange={handleInputChange}
              onAddPendingZeitraum={handleAddPendingZeitraum}
              onUpdatePendingZeitraum={handleUpdatePendingZeitraum}
              onRemovePendingZeitraum={handleRemovePendingZeitraum}
              getPendingZeitraumDateError={getPendingZeitraumDateError}
              onCancel={handleCancelEditing}
              onSave={handleSave}
            />
          </CardContent>
        </Card>
      )}

      {!!auditId && (
        <HistorieTimeline
          entitaetTyp="Audit"
          entitaetId={auditId}
          open={historieOpen}
          onClose={() => setHistorieOpen(false)}
        />
      )}

      {!!auditId && (
        <VersionsHistorie
          type="audit"
          entityId={auditId}
          open={versionenOpen}
          onClose={() => setVersionenOpen(false)}
          onRestore={() => window.location.reload()}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Audit löschen"
        message={
          filesToDelete.length > 0
            ? 'Möchten Sie dieses Audit wirklich löschen? Dabei werden folgende Dateien unwiderruflich mitgelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
            : 'Möchten Sie dieses Audit wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
        }
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setFilesToDelete([]);
        }}
        additionalContent={
          filesToDelete.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  maxHeight: 120,
                  overflow: 'auto',
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                {filesToDelete.map((file) => (
                  <li key={file.id}>{file.dateiname}</li>
                ))}
              </Box>
            </Box>
          ) : undefined
        }
      />

      <ConfirmDialog
        open={deleteZeitraumDialogOpen}
        title="Zeitraum löschen"
        message="Möchten Sie diesen Durchführungszeitraum wirklich löschen?"
        onConfirm={handleDeleteZeitraum}
        onCancel={() => {
          setDeleteZeitraumDialogOpen(false);
          setDeletingZeitraumId(null);
        }}
      />

      <ConfirmDialog
        open={deleteSchwachstelleDialogOpen}
        title="Schwachstelle löschen"
        message={
          filesToDeleteSchwachstelle.length > 0
            ? 'Möchten Sie diese Schwachstelle wirklich löschen? Dabei werden folgende Dateien unwiderruflich mitgelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
            : 'Möchten Sie diese Schwachstelle wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
        }
        onConfirm={handleDeleteSchwachstelle}
        onCancel={() => {
          setDeleteSchwachstelleDialogOpen(false);
          setDeletingSchwachstelleId(null);
          setFilesToDeleteSchwachstelle([]);
        }}
        additionalContent={
          filesToDeleteSchwachstelle.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.5,
                  maxHeight: 120,
                  overflow: 'auto',
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                {filesToDeleteSchwachstelle.map((file) => (
                  <li key={file.id}>{file.dateiname}</li>
                ))}
              </Box>
            </Box>
          ) : undefined
        }
      />

      <ReportReviewWarningDialog
        open={reportWarningOpen}
        findings={unreviewedFindings}
        saving={saving}
        onCancel={() => setReportWarningOpen(false)}
        onGenerateAnyway={handleGenerateReportAnyway}
      />

      <ConfirmDialog
        open={deleteReportDialogOpen}
        title="Report löschen"
        message={
          deletingReportIds.length > 1
            ? 'Möchten Sie diesen Report wirklich löschen? Word- und PDF-Datei werden gemeinsam und unwiderruflich entfernt.'
            : 'Möchten Sie diesen Report wirklich löschen? Die Datei wird unwiderruflich entfernt.'
        }
        onConfirm={handleDeleteReport}
        onCancel={() => {
          setDeleteReportDialogOpen(false);
          setDeletingReportIds([]);
        }}
      />

      <SchwachstelleTransferDialog
        open={transferSchwachstelle != null}
        isDark={isDark}
        schwachstelleId={transferSchwachstelle?.id ?? null}
        schwachstelleTitel={transferSchwachstelle?.titel ?? ''}
        excludeAuditId={auditId}
        currentKundeId={projekte.find((projekt) => projekt.id === audit?.projekt_id)?.kunde?.id ?? null}
        onClose={() => setTransferSchwachstelle(null)}
        onTransferred={(zielAuditName) => {
          setTransferSchwachstelle(null);
          showSuccess(`Schwachstelle nach ${zielAuditName} verschoben`);
          void reloadSchwachstellen();
        }}
      />

      <ZeitraumDialog
        open={zeitraumDialogOpen}
        isDark={isDark}
        editingZeitraum={editingZeitraum}
        zeitraumForm={zeitraumForm}
        mitarbeiter={mitarbeiter}
        zeitraumDateError={zeitraumDateError}
        onClose={handleCloseZeitraumDialog}
        onSave={handleSaveZeitraum}
        onChange={setZeitraumForm}
      />
    </Box>
  );
};

export default AuditDetailPage;
